import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';

export interface MultiSelectOption {
  value: string;
  label: string;
  // Optional grouping — when set, options with the same `group` render under
  // a sticky header inside the dropdown list.
  group?: string;
}

@Component({
  selector: 'app-census-multiselect-dropdown',
  templateUrl: './multiselect-dropdown.component.html',
  styleUrls: ['./multiselect-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiSelectDropdownComponent {
  // Caller-managed: which option values are currently selected. Empty = none.
  @Input() selected: string[] = [];
  @Input() options: MultiSelectOption[] = [];

  // Trigger label shown to the LEFT of the dropdown button (e.g. "FACILITIES").
  @Input() label = '';
  // Text inside the trigger button — caller computes a friendly summary
  // ("All facilities", "3 payer types", etc.) so the multiselect doesn't
  // need to know the noun for each instance.
  @Input() summary = '';

  // Optional in-panel search box — only useful when there are many options
  // (e.g. facilities); turn off for short lists (Action types).
  @Input() searchable = false;
  @Input() searchPlaceholder = 'Search…';
  // When true the trigger is non-interactive (used on tabs where the filter
  // doesn't apply, e.g. Medicare & Managed Care).
  @Input() disabled = false;

  @Output() selectedChange = new EventEmitter<string[]>();

  open = false;
  searchText = '';

  @ViewChild('trigger') private trigger?: ElementRef<HTMLButtonElement>;

  // Stable per-instance prefix for checkbox IDs so labels can target them via
  // `for=` (each instance must be unique on the page).
  readonly idPrefix = `ms-${MultiSelectDropdownComponent.nextInstance++}-`;
  private static nextInstance = 0;

  constructor(
    private host: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef,
  ) {}

  // --- Open / close ---

  toggleOpen(): void {
    if (this.disabled) return;
    this.open = !this.open;
    if (!this.open) this.searchText = '';
    this.cdr.markForCheck();
  }

  // Close when the user clicks outside the dropdown's host element. Each
  // component instance owns its own listener so multiple dropdowns on the
  // page don't interfere with each other.
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.open) return;
    const target = event.target as HTMLElement | null;
    if (!target || !this.host.nativeElement.contains(target)) {
      this.open = false;
      this.cdr.markForCheck();
    }
  }

  // Close on Escape and return focus to the trigger (the focused checkbox is removed).
  @HostListener('keydown.escape')
  onEscape(): void {
    if (!this.open) return;
    this.open = false;
    this.searchText = '';
    this.cdr.markForCheck();
    this.trigger?.nativeElement.focus();
  }

  // --- Selection ---

  isSelected(value: string): boolean {
    return this.selected.includes(value);
  }

  toggle(value: string): void {
    const set = new Set(this.selected);
    if (set.has(value)) set.delete(value);
    else set.add(value);
    this.selectedChange.emit(Array.from(set));
  }

  selectAll(): void {
    this.selectedChange.emit(this.options.map(o => o.value));
  }

  selectNone(): void {
    this.selectedChange.emit([]);
  }

  // --- View helpers ---

  filteredOptions(): MultiSelectOption[] {
    if (!this.searchable || !this.searchText.trim()) return this.options;
    const q = this.searchText.trim().toLowerCase();
    return this.options.filter(o => o.label.toLowerCase().includes(q));
  }

  // Group filteredOptions by their `group` field, preserving first-seen order
  // so callers control the visual grouping by sorting their input array.
  groupedOptions(): Array<{ group: string | null; items: MultiSelectOption[] }> {
    const groups: Array<{ group: string | null; items: MultiSelectOption[] }> = [];
    const indexByGroup = new Map<string | null, number>();
    for (const opt of this.filteredOptions()) {
      const key = opt.group ?? null;
      let idx = indexByGroup.get(key);
      if (idx === undefined) {
        idx = groups.length;
        indexByGroup.set(key, idx);
        groups.push({ group: key, items: [] });
      }
      groups[idx].items.push(opt);
    }
    return groups;
  }

  trackByValue(_index: number, opt: MultiSelectOption): string {
    return opt.value;
  }

  trackByGroup(_index: number, group: { group: string | null }): string {
    return group.group ?? '__ungrouped__';
  }
}
